/**
 * #717 — EL ORDEN DE LAS LÍNEAS DE UNA COTIZACIÓN, EN UN SOLO LUGAR.
 *
 * GEMELO POR ÁRBOL de `mcp-server/src/services/ordenDeLineas.js`. Existe
 * aparte por una única razón: CRA no compila fuera de `src/`, así que el
 * frontend no puede importar del árbol del MCP. Misma forma que
 * `testDataScope.js`, y por el mismo motivo. **La medición completa, el porqué
 * de las tres claves y las tres mediciones contra PostgREST viven en el
 * gemelo** — no se duplican acá para que no puedan divergir.
 *
 * Resumen de lo que sostiene, para el que llega por este árbol:
 *
 *  - El orden de las líneas era indefinido Y NO ESTABLE: editar la descripción
 *    de una línea cambiaba de lugar a otra, sin que nadie tocara un
 *    `sort_order`. PostgREST no garantiza orden en un embed sin `order`.
 *  - Las tres claves son `sort_order` (la intención), `created_at` (que hace
 *    que toda cotización vieja —con sus líneas en el `DEFAULT 0`— se lea en
 *    orden de carga, así que no hace falta backfill) y `id` (porque `now()` es
 *    estable dentro de una transacción: sin la tercera clave, las líneas de un
 *    INSERT masivo vuelven a quedar sin orden definido).
 *  - Un `.order()` pelado apunta al padre y falla ruidoso. No se puede aplicar
 *    al objeto equivocado en silencio.
 */

/** El nombre con el que el embed aparece en el `select`. */
export const EMBED_LINEAS = 'quotation_items';

/**
 * Aplica el orden canónico de las líneas al embed de una consulta.
 *
 * @param {Object} query - PostgrestFilterBuilder ya con su `.select()`
 * @param {string} [embed] - nombre o alias del embed dentro del `select`
 * @returns {Object} la misma consulta, encadenable
 */
export const ordenarLineas = (query, embed = EMBED_LINEAS) =>
  query
    .order('sort_order', { referencedTable: embed, ascending: true })
    .order('created_at', { referencedTable: embed, ascending: true })
    .order('id', { referencedTable: embed, ascending: true });

/**
 * La posición que le toca a una línea nueva cuando el llamador no la eligió.
 *
 * Los caminos de carga MASIVA de este árbol ya asignaban `sort_order: index`;
 * el que agrega UNA línea dejaba el `DEFAULT 0` de la columna, así que una
 * cotización armada línea por línea terminaba con todas en 0 y ordenarla no
 * cambiaba nada. Va en JS y no en un trigger porque Postgres aplica los
 * defaults de columna ANTES del `BEFORE INSERT`: desde la base, un 0 explícito
 * y un parámetro ausente son indistinguibles, y la diferencia importa.
 *
 * @param {Object} client - cliente Supabase
 * @param {string} quotationId
 * @returns {Promise<number>} `max(sort_order) + 1`, o 0 si es la primera línea
 */
export const siguienteSortOrder = async (client, quotationId) => {
  const { data, error } = await client
    .from('quotation_items')
    .select('sort_order')
    .eq('quotation_id', quotationId)
    .order('sort_order', { ascending: false })
    .limit(1);

  // Best-effort declarado: ver el gemelo. Perder la posición es molesto; no
  // poder agregar una línea porque no se pudo LEER una posición es peor.
  if (error) return 0;

  const max = data?.[0]?.sort_order;
  return Number.isFinite(Number(max)) ? Number(max) + 1 : 0;
};

const ordenDeLineas = { EMBED_LINEAS, ordenarLineas, siguienteSortOrder };
export default ordenDeLineas;
